import { useCallback, useEffect, useState } from "react";
import { MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import ShopDialog from "./ShopDialog";

export default function ShopsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/shops/all").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errorText(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    await api.delete(`/shops/${s.id}`);
    toast.success("Shop deleted");
    load();
    onChange?.();
  };

  return (
    <div className="rounded-2xl border border-navy/10 bg-white">
      <div className="flex items-center justify-between border-b border-navy/10 p-4">
        <p className="text-sm text-slate-600">{rows.length} shops · shown on the Shops page, homepage and map</p>
        <button data-testid="admin-shop-add-button" onClick={() => setEditing({})} className="inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark"><Plus className="h-4 w-4" /> Add shop</button>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="admin-shops-table" className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="p-3">Shop</th><th className="p-3">Address</th><th className="p-3">Phone</th><th className="p-3">Map</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-navy/5">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    {s.photo ? <img src={s.photo} alt="" className="h-12 w-16 rounded-lg object-cover" /> : <div className="h-12 w-16 rounded-lg bg-slate-100" />}
                    <span><span className="font-semibold text-navy">{s.name}</span><span className="block text-xs text-slate-500">{s.branch}</span></span>
                  </div>
                </td>
                <td className="p-3 text-slate-600">{[s.address, s.district, s.pincode].filter(Boolean).join(", ") || <span className="italic text-slate-400">Not added</span>}</td>
                <td className="p-3">{s.phone || <span className="italic text-slate-400">—</span>}</td>
                <td className="p-3">{typeof s.lat === "number" ? <MapPin className="h-4 w-4 text-emerald-600" /> : <span className="text-xs text-amber-600">No marker</span>}</td>
                <td className="p-3">{s.active ? <span className="text-emerald-600">Visible</span> : <span className="text-slate-400">Hidden</span>}</td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`admin-shop-edit-${s.id}`} onClick={() => setEditing(s)} className="rounded-lg p-2 hover:bg-slate-100" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`admin-shop-delete-${s.id}`} onClick={() => remove(s)} className="rounded-lg p-2 text-red-600 hover:bg-red-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-500">No shops yet. Click “Add shop” to add your first location.</td></tr>}
          </tbody>
        </table>
      </div>
      <ShopDialog shop={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChange?.(); }} />
    </div>
  );
}
